import {StackableComponent} from 'common/components/base/stackable';
import {defineAndMakeJsxFactory} from 'common/util/jsx';

import {CaretPos} from './types';
import template from './index.jade';
import css from './index.styl?inline';

export const DIRECTIONS = {
  UP: `up`,
  DOWN: `down`,
};

interface DropMenuAttrs {
  open: boolean;
  direction: string;
  responsive: boolean;
  'with-caret': boolean;
  'caret-pos': CaretPos;
  'caret-pos-left': string;
  'use-fixed-pos': boolean;
}

const VERTICAL_OFFSET = 4;

class DropMenu extends StackableComponent<EmptyObj, DropMenuAttrs> {
  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      open: {
        description: `Is the dropdown menu open?`,
        type: `boolean`,
      },
      direction: {
        description: `Menu opens on the top of an element if 'up', on the bottom if 'down'`,
        type: `string`,
        enum: Object.values(DIRECTIONS),
        default: DIRECTIONS.DOWN,
      },
      'with-caret': {
        description: `Show a little caret pointer?`,
        type: `boolean`,
      },
      'caret-pos': {
        description: `position of caret, if caret is shown`,
        type: `json`,
        tsType: `CaretPos`,
        example: {left: `20`},
      },
      'caret-pos-left': {
        description: `DEPRECATED: position of caret from left. use 'caret-pos' as it supports both left and right`,
        type: `string`,
        default: `50%`,
      },
      // TODO @junhouse
      // When a dropmenu is rendered within a container, we need a mechanism to allow the dropmenu to overflow its container.
      // Using fixed position, we temporarily achieve this behavior, mainly in sharing modal. This is an interim solution,
      // while we find a generic solution. When using this attribute with scroll, one needs to make sure to close the dropmenu
      // on scroll event.
      'use-fixed-pos': {
        description: `Use fixed position instead of absolute`,
        type: `boolean`,
      },
      responsive: {
        description: `align menu based on parent and screen space`,
        type: `boolean`,
      },
    };
  }

  get config() {
    return {
      ...super.config,
      css,
      template,
      useShadowDom: true,
      helpers: {
        onBodyPatch: () => {
          this.renderWithResponsivePosition();
        },
        isResponsive: () => {
          return !this.attr(`use-fixed-pos`) && this.attr(`responsive`);
        },
      },
    };
  }

  get trapTab() {
    return false;
  }

  private renderWithFixedPosition() {
    const rect = this.offsetParent.getBoundingClientRect();
    this.style.position = `fixed`;
    this.style.top = `${rect.bottom + VERTICAL_OFFSET}px`;
    this.style.left = `${rect.x}px`;
    this.style.right = ``;
    this.style.bottom = ``;
  }

  private renderWithResponsivePosition() {
    // the menu anchor should always be the closest positioned parent
    const anchorRect = this.offsetParent.getBoundingClientRect();
    const menuRect = this.getBoundingClientRect();
    const styles: Partial<CSSStyleDeclaration> = {
      right: ``,
      bottom: ``,
      top: `${anchorRect.height + VERTICAL_OFFSET}px`,
    };

    if (anchorRect.left + menuRect.width >= window.innerWidth) {
      styles.left = `${this.offsetParent.clientWidth - menuRect.width}px`;
    }

    const hasRoomBelow = anchorRect.bottom + menuRect.height + VERTICAL_OFFSET < window.innerHeight;
    const hasRoomAbove = anchorRect.top - menuRect.height - VERTICAL_OFFSET > 0;

    // only align to top if there's room
    if (!hasRoomBelow && hasRoomAbove) {
      styles.top = `${-menuRect.height - VERTICAL_OFFSET}px`;
    }

    Object.assign(this.style, styles);
  }

  private handleWindowResize = () => this.close();

  open() {
    if (this.attr(`use-fixed-pos`)) {
      this.renderWithFixedPosition();
    } else if (this.attr(`responsive`)) {
      this.renderWithResponsivePosition();
    }

    window.addEventListener(`resize`, this.handleWindowResize);
    super.open();
  }

  close() {
    window.removeEventListener(`resize`, this.handleWindowResize);
    super.close();
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.attr(`open`)) {
      this.open();
    }
  }

  disconnectedCallback() {
    window.removeEventListener(`resize`, this.handleWindowResize);
    super.disconnectedCallback();
  }

  attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);

    if (this.initialized && attr === `open`) {
      if (this.attr(`open`)) {
        this.open();
      } else {
        this.close();
      }
    }
  }

  get caretPos() {
    return this.attr(`caret-pos`) || {left: this.attr(`caret-pos-left`)};
  }
}

export const DropMenuEl = defineAndMakeJsxFactory(`mp-drop-menu`, DropMenu);
